import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, MessageCircle, MapPin, Navigation, Loader2, Tag, X } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import LocationPicker from '@/components/LocationPicker';
import axios from 'axios';

const API = window.location.origin + '/api';

const TIME_SLOTS = [
  { label: 'Morning (8:00 AM - 12:00 PM)', value: 'morning' },
  { label: 'Afternoon (12:00 PM - 4:00 PM)', value: 'afternoon' },
  { label: 'Evening (4:00 PM - 8:00 PM)', value: 'evening' },
];

function getNextDays(count = 7) {
  const days = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    days.push({ label, value: d.toISOString().split('T')[0] });
  }
  return days;
}

export default function Checkout() {
  const { cartItems, getCartTotal, fetchCart } = useCart();
  const { user } = useAuth();
  const { selectedStore, selectedBranch } = useStore();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = useState(true);
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliverySlot, setDeliverySlot] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [error, setError] = useState('');
  const [waLink, setWaLink] = useState('');
  const [deliveryZone, setDeliveryZone] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [feeConfig, setFeeConfig] = useState(null);
  const [feeMethod, setFeeMethod] = useState('');
  const [feeReason, setFeeReason] = useState('');
  const [distanceKm, setDistanceKm] = useState(null);
  const [customerLoc, setCustomerLoc] = useState(null);  // { lat, lng } | null
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [days] = useState(() => getNextDays(7));

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, message, discount_amount, delivery_fee_discount }
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // Initial mount: fetch cart, user prefill, defaults, WA link, fee config
  useEffect(() => {
    fetchCart();
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || '');
    }
    if (days.length > 0) setDeliveryDate(days[0].value);
    setDeliverySlot('morning');
    axios.get(`${API}/whatsapp/order-link`).then(r => {
      if (r.data.configured) setWaLink(r.data.link);
    }).catch(err => console.error('Failed to load WhatsApp link:', err));
    axios.get(`${API}/delivery-fee?order_total=0`).then(r => {
      setFeeConfig(r.data.config);
    }).catch(err => console.error('Failed to load delivery fee config:', err));
  }, [user, fetchCart, days]);

  // Recalculate delivery fee when cart total / zone / customer location / branch changes
  const cartTotal = getCartTotal();
  const recalcFee = useCallback(() => {
    const params = new URLSearchParams({ order_total: String(cartTotal) });
    if (customerLoc?.lat != null && customerLoc?.lng != null && selectedBranch?.id) {
      params.set('branch_id', selectedBranch.id);
      params.set('customer_lat', String(customerLoc.lat));
      params.set('customer_lng', String(customerLoc.lng));
    } else if (deliveryZone) {
      params.set('zone', deliveryZone);
    }
    axios.get(`${API}/delivery-fee?${params.toString()}`).then(r => {
      setDeliveryFee(r.data.fee);
      setFeeMethod(r.data.method || '');
      setFeeReason(r.data.reason || '');
      setDistanceKm(typeof r.data.distance_km === 'number' ? r.data.distance_km : null);
    }).catch(err => console.error('Failed to recalc delivery fee:', err));
  }, [cartTotal, deliveryZone, customerLoc, selectedBranch]);

  useEffect(() => { recalcFee(); }, [recalcFee]);

  const handleUseGps = useCallback(() => {
    setGpsError('');
    if (!navigator.geolocation) {
      setGpsError('GPS is not supported on this browser. Please drop a pin on the map instead.');
      setShowMap(true);
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCustomerLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsLoading(false);
        setShowMap(true);
      },
      (err) => {
        setGpsLoading(false);
        setGpsError(err.message || 'Could not get GPS location. Drop a pin on the map instead.');
        setShowMap(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  // Re-validate applied coupon whenever the cart or fee changes (min-order might now fail)
  useEffect(() => {
    if (!appliedCoupon) return;
    axios.post(`${API}/coupons/validate`, {
      code: appliedCoupon.code,
      subtotal: getCartTotal(),
      delivery_fee: deliveryFee,
      supermarket_id: selectedStore?.id || '',
    }, { withCredentials: true })
      .then(({ data }) => setAppliedCoupon(data))
      .catch(err => {
        setAppliedCoupon(null);
        setCouponError(err.response?.data?.detail || 'Coupon no longer valid');
      });
  }, [cartTotal, deliveryFee, selectedStore?.id, appliedCoupon, getCartTotal]);

  if (cartItems.length === 0 && !success) {
    return (
      <div className="checkout-page">
        <div className="empty-state">
          <h3>Your cart is empty</h3>
          <p>Add items before checking out</p>
          <button className="btn-lime" onClick={() => navigate('/')} style={{ maxWidth: '200px', margin: '0 auto' }}>Shop Now</button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedBranch?.id) {
      setError('Please choose a branch first. Tap "Choose a branch" above to pick your nearest centre.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        delivery_name: name,
        delivery_phone: phone,
        delivery_address: address,
        delivery_date: deliveryDate,
        delivery_time_slot: deliverySlot,
        notes,
        branch_id: selectedBranch.id,
        branch_name: selectedBranch.name,
        whatsapp_number: whatsappSameAsPhone ? phone : whatsapp,
        customer_lat: customerLoc?.lat ?? null,
        customer_lng: customerLoc?.lng ?? null,
        delivery_fee: deliveryFee,
        distance_km: distanceKm,
        fee_method: feeMethod,
        coupon_code: appliedCoupon?.code || '',
      }, { withCredentials: true });
      setSuccess(true);
      setOrderId(data.id || '');
      await fetchCart();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = async () => {
    const code = (couponInput || '').trim().toUpperCase();
    setCouponError('');
    if (!code) {
      setCouponError('Enter a coupon code');
      return;
    }
    setCouponLoading(true);
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code,
        subtotal: getCartTotal(),
        delivery_fee: deliveryFee,
        supermarket_id: selectedStore?.id || '',
      }, { withCredentials: true });
      setAppliedCoupon(data);
      setCouponInput('');
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Could not apply coupon');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  const getWhatsAppOrderMsg = () => {
    const items = cartItems.map(i => `${i.product?.name} x${i.quantity}`).join(', ');
    const slotLabel = TIME_SLOTS.find(s => s.value === deliverySlot)?.label || deliverySlot;
    const dateLabel = days.find(d => d.value === deliveryDate)?.label || deliveryDate;
    return encodeURIComponent(`Hi StoreRuns! I'd like to place an order:\n\nItems: ${items}\nTotal: ${getCartTotal().toFixed(2)} PGK\nDelivery: ${dateLabel}, ${slotLabel}\nAddress: ${address || '(will provide)'}\nName: ${name}\nPhone: ${phone}`);
  };

  if (success) {
    return (
      <div className="checkout-page" data-testid="order-success">
        <div style={{ textAlign: 'center', padding: '48px 20px' }}>
          <CheckCircle size={64} style={{ color: 'var(--success)', marginBottom: '16px' }} />
          <h2 style={{ color: 'var(--navy)', marginBottom: '8px' }}>Order Placed!</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Your order has been placed successfully. We&apos;ll start preparing it right away!</p>
          {waLink && (
            <a
              href={`${waLink}?text=${encodeURIComponent(`Hi! I just placed order #${orderId.slice(0,8)} on StoreRuns. Please confirm delivery schedule.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
              data-testid="whatsapp-confirm-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#25D366', color: 'white', padding: '12px 24px', borderRadius: '12px', textDecoration: 'none', fontWeight: 600, marginBottom: '16px' }}
            >
              <MessageCircle size={20} /> Confirm via WhatsApp
            </a>
          )}
          <div>
            <button className="btn-primary" style={{ maxWidth: '240px' }} onClick={() => navigate('/orders')} data-testid="view-orders-btn">
              View My Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page" data-testid="checkout-page">
      <button className="back-btn" onClick={() => navigate('/cart')} data-testid="back-to-cart-btn">
        <ArrowLeft /> Back to Cart
      </button>
      <h2>Checkout</h2>
      {selectedStore && (
        <div data-testid="checkout-branch-info" style={{
          padding: '12px 14px', marginBottom: '14px', borderRadius: 12,
          background: selectedBranch ? '#ECFDF5' : '#FEF3C7',
          border: `1px solid ${selectedBranch ? '#A7F3D0' : '#FCD34D'}`,
          color: selectedBranch ? '#065F46' : '#92400E',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap'
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.86rem', fontWeight: 600 }}>
            <MapPin size={15} />
            {selectedBranch
              ? <>Sourcing from <strong>{selectedBranch.name}</strong></>
              : <>No branch chosen for <strong>{selectedStore.name}</strong></>
            }
          </span>
          <button
            type="button"
            onClick={() => navigate(`/store/${selectedStore.slug}`)}
            data-testid="checkout-change-branch-btn"
            style={{ background: 'transparent', border: '1px solid currentColor', color: 'inherit', padding: '4px 10px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {selectedBranch ? 'Change' : 'Choose branch'}
          </button>
        </div>
      )}
      <div className="cart-summary" style={{ marginBottom: '20px' }}>
        <p style={{ fontWeight: 600, marginBottom: '8px' }}>Order Summary</p>
        {cartItems.map(item => (
          <div key={item.product_id} className="cart-summary-row" style={{ fontSize: '0.85rem' }}>
            <span>{item.product?.name} x{item.quantity}</span>
            <span>{(item.weight_price != null ? item.weight_price : ((item.product?.discount_percent > 0 ? item.product.price * (1 - item.product.discount_percent / 100) : item.product?.price || 0) * item.quantity)).toFixed(2)} PGK</span>
          </div>
        ))}
        <div className="cart-summary-row" style={{ fontSize: '0.85rem' }}>
          <span>Subtotal</span>
          <span>{getCartTotal().toFixed(2)} PGK</span>
        </div>
        <div className="cart-summary-row" style={{ fontSize: '0.85rem' }}>
          <span>
            Delivery Fee
            {feeReason && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>{feeReason}</div>}
          </span>
          <span>{deliveryFee > 0 ? `K${deliveryFee.toFixed(2)}` : 'Free'}</span>
        </div>
        {appliedCoupon && (
          <div className="cart-summary-row" style={{ fontSize: '0.85rem', color: '#065F46' }} data-testid="coupon-summary-row">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Tag size={13} /> Coupon <strong>{appliedCoupon.code}</strong>
            </span>
            <span>−K{((appliedCoupon.discount_amount || 0) + (appliedCoupon.delivery_fee_discount || 0)).toFixed(2)}</span>
          </div>
        )}
        <div className="cart-summary-row cart-summary-total">
          <span>Total</span>
          <span data-testid="checkout-grand-total">{
            Math.max(
              getCartTotal() + deliveryFee - ((appliedCoupon?.discount_amount || 0) + (appliedCoupon?.delivery_fee_discount || 0)),
              0
            ).toFixed(2)
          } PGK</span>
        </div>
      </div>

      {/* Coupon input */}
      <div style={{ background: 'var(--white)', border: '1px solid var(--border-gray)', borderRadius: 12, padding: 14, marginBottom: 20 }} data-testid="coupon-section">
        {appliedCoupon ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }} data-testid="coupon-applied">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
              <div style={{ background: '#D1FAE5', color: '#065F46', padding: '4px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Tag size={13} /> <strong style={{ fontSize: '0.82rem' }}>{appliedCoupon.code}</strong>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#065F46' }}>{appliedCoupon.message}</div>
            </div>
            <button
              type="button"
              onClick={removeCoupon}
              data-testid="coupon-remove-btn"
              style={{ background: 'transparent', border: '1px solid var(--border-gray)', color: 'var(--text-muted)', padding: '5px 10px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <X size={12} /> Remove
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', flexShrink: 0 }}>
                <Tag size={16} />
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Coupon code</span>
              </div>
              <input
                type="text"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                placeholder="e.g. WELCOME10"
                data-testid="coupon-input"
                style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-gray)', fontSize: '0.9rem', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              />
              <button
                type="button"
                onClick={applyCoupon}
                disabled={couponLoading || !couponInput.trim()}
                data-testid="coupon-apply-btn"
                style={{ padding: '9px 16px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: couponLoading ? 'wait' : 'pointer', opacity: couponLoading || !couponInput.trim() ? 0.6 : 1 }}
              >
                {couponLoading ? '…' : 'Apply'}
              </button>
            </div>
            {couponError && <div style={{ marginTop: 8, fontSize: '0.78rem', color: '#B91C1C' }} data-testid="coupon-error">{couponError}</div>}
          </>
        )}
      </div>

      {error && <div className="error-msg">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Delivery Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" required data-testid="checkout-name-input" />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MessageCircle size={14} style={{ color: '#25D366' }} /> WhatsApp Number
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={whatsappSameAsPhone}
              onChange={e => setWhatsappSameAsPhone(e.target.checked)}
              data-testid="whatsapp-same-as-phone"
              style={{ width: 'auto', margin: 0, cursor: 'pointer' }}
            />
            Same as my phone number below
          </label>
          {!whatsappSameAsPhone && (
            <input
              value={whatsapp}
              onChange={e => setWhatsapp(e.target.value)}
              placeholder="+675 7000 0000"
              required
              data-testid="checkout-whatsapp-input"
            />
          )}
          <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 6 }}>
            We&apos;ll coordinate delivery and confirmations with you on WhatsApp.
          </p>
        </div>
        <div className="form-group">
          <label>Phone Number</label>
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+675 1234 5678" required data-testid="checkout-phone-input" />
        </div>
        <div className="form-group">
          <label>Delivery Address</label>
          <textarea value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter your full delivery address" required rows={3} data-testid="checkout-address-input" />
        </div>

        {/* Auto Location → Distance-Based Fee */}
        <div className="location-block" data-testid="location-block" style={{
          background: 'var(--white)', border: '1px solid var(--border-gray)',
          borderRadius: 12, padding: '14px 16px', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--navy)', fontWeight: 700, fontSize: '0.92rem' }}>
              <Navigation size={15} /> Set your location for accurate delivery fee
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button type="button" onClick={handleUseGps} disabled={gpsLoading}
                data-testid="use-gps-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '7px 12px', borderRadius: 999, border: '1.5px solid var(--lime)',
                  background: 'var(--lime)', color: 'var(--navy)',
                  fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                {gpsLoading ? <Loader2 size={13} className="spin" /> : <Navigation size={13} />}
                {gpsLoading ? 'Locating…' : 'Use my GPS'}
              </button>
              <button type="button" onClick={() => setShowMap(s => !s)}
                data-testid="toggle-map-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '7px 12px', borderRadius: 999, border: '1.5px solid var(--navy)',
                  background: 'var(--white)', color: 'var(--navy)',
                  fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                <MapPin size={13} /> {showMap ? 'Hide map' : 'Pin on map'}
              </button>
            </div>
          </div>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
            We&apos;ll measure the distance from <strong>{selectedBranch?.name || 'your selected branch'}</strong> to your location and calculate the delivery fee automatically.
          </p>
          {gpsError && (
            <div data-testid="gps-error" style={{ marginTop: 8, fontSize: '0.78rem', color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', padding: '6px 10px', borderRadius: 8 }}>
              {gpsError}
            </div>
          )}
          {customerLoc && (
            <div data-testid="location-confirmed" style={{
              marginTop: 10, padding: '8px 12px', borderRadius: 8,
              background: '#ECFDF5', border: '1px solid #A7F3D0',
              color: '#065F46', fontSize: '0.82rem', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'
            }}>
              <CheckCircle size={14} />
              <span>Location set</span>
              {distanceKm != null && (
                <span style={{ fontWeight: 700 }}>· {distanceKm.toFixed(2)} km from branch · K{deliveryFee} fee</span>
              )}
            </div>
          )}
          {showMap && (
            <div style={{ marginTop: 12 }}>
              <LocationPicker
                value={customerLoc}
                onChange={(loc) => { setCustomerLoc(loc); setGpsError(''); }}
                center={selectedBranch?.lat && selectedBranch?.lng ? [selectedBranch.lat, selectedBranch.lng] : [-9.4438, 147.1803]}
                height={260}
              />
              <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: '6px 0 0' }}>
                Tap or drag the pin to your exact location. Free OpenStreetMap — no Google account needed.
              </p>
            </div>
          )}
        </div>

        {/* Delivery Zone (manual fallback when no GPS / map pin) */}
        {feeConfig && feeConfig.zones && !customerLoc && (
          <div className="form-group">
            <label>Delivery Zone <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)', fontWeight: 400 }}>(skip if you set location above)</span></label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }} data-testid="delivery-zone-selector">
              {Object.entries(feeConfig.zones).map(([key, zone]) => (
                <button key={key} type="button" onClick={() => setDeliveryZone(key)}
                  data-testid={`zone-${key}`}
                  style={{
                    padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border-gray)',
                    background: deliveryZone === key ? 'var(--navy)' : 'var(--white)',
                    color: deliveryZone === key ? 'white' : 'var(--text-dark)',
                    fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer',
                    flex: 1, minWidth: '120px', textAlign: 'center'
                  }}>
                  {zone.name}<br /><span style={{ fontSize: '0.72rem', opacity: 0.8 }}>K{zone.fee} fee</span>
                </button>
              ))}
            </div>
            {getCartTotal() >= feeConfig.free_above && (
              <p style={{ fontSize: '0.78rem', color: 'var(--success)', marginTop: '6px', fontWeight: 500 }}>
                Free delivery on orders over K{feeConfig.free_above}!
              </p>
            )}
          </div>
        )}

        <div className="delivery-schedule-section" style={{ background: 'var(--white)', border: '1px solid var(--border-gray)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
          <h3 style={{ color: 'var(--navy)', fontSize: '1rem', margin: '0 0 12px', fontWeight: 600 }}>Delivery Schedule</h3>
          <div className="form-group">
            <label>Delivery Date</label>
            <div className="date-chips" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }} data-testid="delivery-date-selector">
              {days.map(d => (
                <button
                  type="button"
                  key={d.value}
                  onClick={() => setDeliveryDate(d.value)}
                  data-testid={`date-chip-${d.value}`}
                  style={{
                    padding: '8px 14px', borderRadius: '20px', border: '1px solid var(--border-gray)',
                    background: deliveryDate === d.value ? 'var(--navy)' : 'var(--white)',
                    color: deliveryDate === d.value ? 'white' : 'var(--text-dark)',
                    fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Time Slot</label>
            <div className="slot-chips" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }} data-testid="delivery-slot-selector">
              {TIME_SLOTS.map(s => (
                <button
                  type="button"
                  key={s.value}
                  onClick={() => setDeliverySlot(s.value)}
                  data-testid={`slot-chip-${s.value}`}
                  style={{
                    padding: '10px 16px', borderRadius: '10px', border: '1px solid var(--border-gray)',
                    background: deliverySlot === s.value ? 'var(--lime)' : 'var(--white)',
                    color: deliverySlot === s.value ? 'white' : 'var(--text-dark)',
                    fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer',
                    transition: 'all 0.15s', flex: '1', minWidth: '140px', textAlign: 'center'
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Special Instructions (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any special requests?" rows={2} data-testid="checkout-notes-input" />
        </div>
        <button type="submit" className="btn-lime" disabled={loading || !selectedBranch} data-testid="place-order-btn">
          {loading ? 'Placing Order...' : (!selectedBranch ? 'Choose a branch to continue' : `Place Order - ${(getCartTotal() + deliveryFee).toFixed(2)} PGK`)}
        </button>

        {waLink && (
          <a
            href={`${waLink}?text=${getWhatsAppOrderMsg()}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-order-btn"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#25D366', color: 'white', padding: '13px', borderRadius: '12px', textDecoration: 'none', fontWeight: 600, marginTop: '10px', fontSize: '0.95rem' }}
          >
            <MessageCircle size={20} /> Order via WhatsApp
          </a>
        )}
      </form>
    </div>
  );
}
